import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input } from "reactstrap";
import MainLayout from "../components/layouts/MainLayout";
import api from "../api/api";
import Swal from "sweetalert2";
import "../assets/scss/LinksPage.scss";

const LinksPage = () => {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingLink, setEditingLink] = useState(null);
    const [formData, setFormData] = useState({
        label: "",
        url: "",
        description: "",
        category: "Descàrregues",
        icon: "bi-link-45deg",
        tags: "",
        previewImage: ""
    });

    const [uploadingImage, setUploadingImage] = useState(false);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formDataUpload = new FormData();
        formDataUpload.append("image", file);

        setUploadingImage(true);
        try {
            const response = await api.post("/links/upload", formDataUpload, {
                headers: {
                    "Content-Type": "multipart/form-data"
                }
            });
            if (response.data.operation === "SUCCESS") {
                setFormData(prev => ({ ...prev, previewImage: response.data.filePath }));
                Swal.fire({
                    icon: 'success',
                    title: 'Imatge pujada',
                    timer: 1500,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            Swal.fire("Error", "No s'ha pogut pujar la imatge", "error");
        } finally {
            setUploadingImage(false);
        }
    };

    const fetchLinks = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get("/links");
            if (response.data.operation === "SUCCESS") {
                setLinks(response.data.links || []);
            }
        } catch (error) {
            console.error("Error fetching links:", error);
            Swal.fire("Error", "No s'han pogut carregar els enllaços", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLinks();
    }, [fetchLinks]);

    const handleOpenModal = (link = null) => {
        if (link) {
            setEditingLink(link);
            setFormData({
                label: link.label,
                url: link.url,
                description: link.description,
                category: link.category,
                icon: link.icon,
                tags: link.tags ? link.tags.join(", ") : "",
                previewImage: link.previewImage || ""
            });
        } else {
            setEditingLink(null);
            setFormData({
                label: "",
                url: "",
                description: "",
                category: "Descàrregues",
                icon: "bi-link-45deg",
                tags: "",
                previewImage: ""
            });
        }
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Eliminar enllaç?',
            text: "Esta acció no es pot desfer.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancel·lar'
        });

        if (result.isConfirmed) {
            try {
                await api.delete(`/links/${id}`);
                Swal.fire("Eliminat", "L'enllaç ha sigut eliminat", "success");
                fetchLinks();
            } catch (error) {
                Swal.fire("Error", "No s'ha pogut eliminar l'enllaç", "error");
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        const payload = {
            ...formData,
            tags: formData.tags.split(",").map(tag => tag.trim()).filter(tag => tag !== "")
        };

        try {
            if (editingLink) {
                await api.put(`/links/${editingLink._id}`, payload);
                Swal.fire("Actualitzat", "Enllaç actualitzat correctament", "success");
            } else {
                await api.post("/links", payload);
                Swal.fire("Creat", "Nou enllaç creat correctament", "success");
            }
            setShowModal(false);
            fetchLinks();
        } catch (error) {
            Swal.fire("Error", "S'ha produït un error al guardar", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Group links by category
    const categories = [...new Set(links.map(link => link.category))];

    return (
        <MainLayout>
            <div className="links-page-container">
                <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-5">
                    <div>
                        <h2 className="fw-bold text-dark mb-1">Recursos i Enllaços</h2>
                        <p className="text-muted small mb-0">Gestiona els teus accessos directes i eines preferides.</p>
                    </div>
                    <Button color="primary" className="rounded-pill px-4" onClick={() => handleOpenModal()}>
                        <i className="bi bi-plus-lg me-2"></i> Nou Enllaç
                    </Button>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status"></div>
                    </div>
                ) : links.length === 0 ? (
                    <div className="text-center py-5">
                        <p className="text-muted">No hi ha enllaços guardats. Comença creant-ne un!</p>
                    </div>
                ) : categories.map(category => (
                    <div key={category} className="mb-5">
                        <h4 className="category-title mb-4">
                            <span className="title-dot"></span>
                            {category}
                        </h4>
                        <Row>
                            {links.filter(link => link.category === category).map(link => (
                                <Col key={link._id} xl={4} lg={6} md={6} className="mb-4">
                                    <Card className="link-card-premium h-100 border-0 shadow-sm transition-all overflow-hidden position-relative">
                                        {/* Action Buttons Overlay */}
                                        <div className="card-actions-overlay">
                                            <button className="btn-action-circle edit" onClick={() => handleOpenModal(link)}>
                                                <i className="bi bi-pencil-fill"></i>
                                            </button>
                                            <button className="btn-action-circle delete" onClick={() => handleDelete(link._id)}>
                                                <i className="bi bi-trash-fill"></i>
                                            </button>
                                        </div>

                                        <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none h-100 d-flex flex-column">
                                            {/* Website Preview Header */}
                                            <div className="card-preview-header position-relative">
                                                <img 
                                                    src={link.previewImage ? (link.previewImage.startsWith('http') ? link.previewImage : `${process.env.REACT_APP_API_URL || 'https://series.cryptotutia.com/api'}${link.previewImage.startsWith('/api') ? link.previewImage.replace('/api', '') : link.previewImage}`) : `https://image.thum.io/get/width/600/crop/800/noanimate/${link.url}`} 
                                                    alt={link.label}
                                                    className="w-100 preview-img"
                                                    loading="lazy"
                                                />
                                                <div className="preview-overlay d-flex align-items-center justify-content-center">
                                                    <i className="bi bi-box-arrow-up-right fs-2 text-white"></i>
                                                </div>
                                                <div className={`category-badge ${link.className || ''}`} style={{ background: '#038edc', color: 'white' }}>
                                                    <i className={`bi ${link.icon || 'bi-link'} me-1`}></i>
                                                    {link.label}
                                                </div>
                                            </div>

                                            <CardBody className="p-4 flex-grow-1 d-flex flex-column">
                                                <h5 className="mb-2 fw-bold text-dark">{link.label}</h5>
                                                <p className="text-muted small mb-3 description-text">
                                                    {link.description}
                                                </p>
                                                
                                                {/* Tags Section */}
                                                <div className="d-flex flex-wrap gap-1 mt-auto">
                                                    {link.tags && link.tags.map(tag => (
                                                        <Badge key={tag} pill className="tag-badge">
                                                            {tag}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </CardBody>
                                        </a>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </div>
                ))}
            </div>

            {/* FORM MODAL */}
            <Modal isOpen={showModal} toggle={() => setShowModal(false)} centered size="lg" className="link-management-modal">
                <ModalHeader toggle={() => setShowModal(false)}>
                    {editingLink ? "Editar Enllaç" : "Afegir Nou Enllaç"}
                </ModalHeader>
                <ModalBody className="p-4">
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Nom de la Web</Label>
                                    <Input 
                                        type="text" 
                                        value={formData.label} 
                                        onChange={(e) => setFormData({...formData, label: e.target.value})}
                                        required
                                        placeholder="Ej: FilmAffinity"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>URL</Label>
                                    <Input 
                                        type="url" 
                                        value={formData.url} 
                                        onChange={(e) => setFormData({...formData, url: e.target.value})}
                                        required
                                        placeholder="https://..."
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <FormGroup>
                            <Label>Descripció</Label>
                            <Input 
                                type="textarea" 
                                value={formData.description} 
                                onChange={(e) => setFormData({...formData, description: e.target.value})}
                                placeholder="Explica breument què ofereix esta web..."
                                rows={3}
                            />
                        </FormGroup>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Categoria</Label>
                                    <Input 
                                        type="select" 
                                        value={formData.category} 
                                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                                    >
                                        <option>Descàrregues</option>
                                        <option>Informació</option>
                                        <option>Eines</option>
                                        <option>Notícies</option>
                                        <option>Altres</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Icona (Bootstrap Icon)</Label>
                                    <Input 
                                        type="text" 
                                        value={formData.icon} 
                                        onChange={(e) => setFormData({...formData, icon: e.target.value})}
                                        placeholder="bi-link-45deg"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <FormGroup>
                            <Label>Etiquetes (separades per comes)</Label>
                            <Input 
                                type="text" 
                                value={formData.tags} 
                                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                                placeholder="Ej: DHT, 4K, Subtítols"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Imatge de Previsualització (Fitxer o URL)</Label>
                            <div className="d-flex gap-2 align-items-center mb-2">
                                <Input 
                                    type="file" 
                                    onChange={handleImageUpload}
                                    accept="image/*"
                                    className="d-none"
                                    id="imageUpload"
                                />
                                <Button 
                                    color="info" 
                                    outline 
                                    size="sm" 
                                    onClick={() => document.getElementById('imageUpload').click()}
                                    disabled={uploadingImage}
                                >
                                    <i className="bi bi-upload me-2"></i>
                                    {uploadingImage ? "Pujant..." : "Pujar Imatge (jpg, png)"}
                                </Button>
                                {formData.previewImage && (
                                    <Badge color="success" pill>
                                        <i className="bi bi-check2 me-1"></i> Imatge llista
                                    </Badge>
                                )}
                            </div>
                            <small className="text-muted d-block mb-2">O bé pega una URL directa:</small>
                            <Input 
                                type="text" 
                                value={formData.previewImage} 
                                onChange={(e) => setFormData({...formData, previewImage: e.target.value})}
                                placeholder="https://... o ruta del fitxer pujat"
                            />
                            {formData.previewImage && (
                                <div className="mt-2 text-center p-2 border rounded bg-light">
                                    <img 
                                        src={formData.previewImage.startsWith('http') ? formData.previewImage : `${process.env.REACT_APP_API_URL || 'https://series.cryptotutia.com/api'}${formData.previewImage.startsWith('/api') ? formData.previewImage.replace('/api', '') : formData.previewImage}`} 
                                        alt="Preview" 
                                        style={{ maxHeight: '100px', borderRadius: '4px' }}
                                    />
                                    <div className="mt-1 small text-muted">Previsualització del fitxer</div>
                                </div>
                            )}
                        </FormGroup>
                        <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                            <Button color="light" onClick={() => setShowModal(false)}>Cancel·lar</Button>
                            <Button color="primary" type="submit" disabled={isSubmitting}>
                                {isSubmitting ? "Guardant..." : (editingLink ? "Actualitzar Enllaç" : "Crear Enllaç")}
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>
        </MainLayout>
    );
};

export default LinksPage;
